import { ref, watch, nextTick, shallowRef, triggerRef, type ComputedRef, type Ref, computed } from 'vue';
import type { GlobalStore } from 'src/stores/global-store';
import type { UserEntryViewMeta } from '@cognitoforms/types/server-types/core/services/user-entry-view-meta';
import type { UserInfo } from '@cognitoforms/types/server-types/forms/model/user-info';
import type { FormEntryIndex } from '@cognitoforms/types/server-types/forms/model/form-entry-index';
import type { FormEntry } from '@cognitoforms/types/server-types/forms/model/form-entry';
import type { AdminApi } from '../../../../../client/src/embed-scripts/admin';
import { Deferred } from 'src/utilities/deferred';
import { getViewId, getFormId, getViewToken, getEntryKeyFromIndex, getTaskViewRoleId, getEntryKey } from './use-task-utils';
import type { EntrySubmissionResult } from '../../../../../client/src/web-api/entry-service';
import type { UserFormMeta } from '@cognitoforms/types/server-types/core/services/user-form-meta';
import { showConfirmationMessage } from './confirmation-messages';

type UseTaskActionsOptions = {
	globalStore: GlobalStore;
	adminApi: Ref<AdminApi>,
	tasks: Ref<FormEntryIndex[]>,
	taskViews: ComputedRef<UserEntryViewMeta[]>,
	formsWithTaskViews: ComputedRef<UserFormMeta[]>,
	userInfo: ComputedRef<UserInfo>,
	inlineActionName: Ref<string>,
	selectedEntry: Ref<FormEntry>,
	getEntry: (index : FormEntryIndex) => Promise<FormEntry>,
	onTaskClick: (index : FormEntryIndex, showErrorMessage: boolean) => Promise<void>,
	visibleTasks: Set<string>
}

export type DisplayAction = {
	value: string,
	displayValue: string
};

export function useTaskActions(options: UseTaskActionsOptions) {
	const getAllowedActions = async (index: FormEntryIndex) : Promise<DisplayAction[]> => {
		const viewId = getViewId(index);
		const cancelTask = new Deferred();

		const allowedActions = [];
		await options.adminApi.value.getAllowedActions(viewId, [getEntryKeyFromIndex(index)], getTaskViewRoleId(viewId, options.taskViews), options.userInfo.value, getViewToken(viewId, options.taskViews), (actionName) => allowedActions.push(actionName), cancelTask.promise);

		return allowedActions.map(actionName => {
			return { value: actionName, displayValue: actionName };
		});
	};

	const getAllowedActionsForView = async (viewId: string) : Promise<DisplayAction[]> => {
		const cancelTask = new Deferred();

		const entryKeys = options.tasks.value.filter(task => getViewId(task) === viewId).map(task => getEntryKeyFromIndex(task));
		const allowedActions = [];
		if (entryKeys.length > 0)
			await options.adminApi.value.getAllowedActions(viewId, entryKeys, getTaskViewRoleId(viewId, options.taskViews), options.userInfo.value, getViewToken(viewId, options.taskViews), (actionName) => allowedActions.push(actionName), cancelTask.promise);

		return allowedActions.map(actionName => {
			return { value: actionName, displayValue: actionName };
		});
	};

	const indexIdsWithActionsLoaded = shallowRef(new Set<string>());
	const bulkActionsMap = shallowRef(new Map<string, Array<DisplayAction>>());
	const onActionsLoaded = (eventData: { viewId: string, actions: DisplayAction[], taskId?: string }) => {
		const { viewId, actions, taskId } = eventData;

		if (taskId) {
			indexIdsWithActionsLoaded.value.add(taskId);
			triggerRef(indexIdsWithActionsLoaded);
		}

		const existingActions = bulkActionsMap.value.get(viewId);
		if (!existingActions) {
			bulkActionsMap.value.set(viewId, actions);
			triggerRef(bulkActionsMap);
		}
		else {
			const mergedActions = [
				...existingActions,
				...actions.filter(
					(newAction) => !existingActions.some((existingAction) => existingAction.value === newAction.value)
				)
			];

			bulkActionsMap.value.set(viewId, mergedActions);
			triggerRef(bulkActionsMap);
		}
	};

	const allActionsLoadedMap = shallowRef(new Map<string, boolean>());
	const handleLoadAllActions = async (viewId: string) => {
		const actions = await getAllowedActionsForView(viewId);
		const eventData = {
			viewId: viewId,
			actions: actions
		};
		onActionsLoaded(eventData);

		allActionsLoadedMap.value.set(viewId, true);
		triggerRef(allActionsLoadedMap);
	};

	watch(indexIdsWithActionsLoaded, (indexIdsWithActionsLoaded) => {
		options.taskViews.value.map(taskView => taskView.Id).forEach(viewId => {
			const isAllTasksScrolledIntoView = options.tasks.value.filter(task => getViewId(task) === viewId).every(task => [...indexIdsWithActionsLoaded].some(taskId => taskId === task.Id));
			if (isAllTasksScrolledIntoView) {
				allActionsLoadedMap.value.set(viewId, true);
				triggerRef(allActionsLoadedMap);
			}
			// if a task has been 'scrolled in' in an entry view, we need to ensure we load all actions for the entries for the bulk action button
			// don't re-fetch all actions if we have already fetched all actions for that view
			else if (!allActionsLoadedMap.value.get(viewId) && bulkActionsMap.value.get(viewId)?.length === 0 && [...options.visibleTasks].some(task => task.startsWith(viewId))) {
				handleLoadAllActions(viewId);
				allActionsLoadedMap.value.set(viewId, true);
			}
		});
	});

	watch(() => options.formsWithTaskViews.value, async (updatedFormsWithTaskViews, previousFormsWithTaskViews) => {
		if (![...allActionsLoadedMap.value.values()].some(allActionsLoaded => allActionsLoaded))
			return;

		const previousTaskViewIds = previousFormsWithTaskViews.flatMap(previousForm => previousForm.EntryViews.filter(entryView => entryView.IsTaskView).map(entryView => entryView.Id));
		const updatedTaskViewIds = updatedFormsWithTaskViews.flatMap(updatedForm => updatedForm.EntryViews.filter(entryView => entryView.IsTaskView).map(entryView => entryView.Id));
		const removedTaskViewIds = previousTaskViewIds.filter(previousTaskViewId => !updatedTaskViewIds.includes(previousTaskViewId));
		const taskViewIdsToRefresh = updatedTaskViewIds.filter(updatedTaskViewId => !removedTaskViewIds.includes(updatedTaskViewId));

		for (const [viewId, isLoaded] of allActionsLoadedMap.value.entries()) {
			if (isLoaded && taskViewIdsToRefresh.includes(viewId)) {
				const actions = await getAllowedActionsForView(viewId);
				bulkActionsMap.value.set(viewId, actions);
				triggerRef(bulkActionsMap);
			}
		}
	});

	const inlineActionIndex = ref(null);
	const bulkActionInProgress = ref(false);
	let inlineActionFailed = false;
	const performInlineAction = async (eventData: { actionName: string, index: FormEntryIndex }) => {
		if (inlineActionIndex.value || bulkActionInProgress.value)
			return;

		const { actionName, index } = eventData;
		const viewId = getViewId(index);

		inlineActionIndex.value = index;
		options.inlineActionName.value = actionName;
		try {
			const submissionResult = await options.adminApi.value.performAction(getFormId(index), getEntryKeyFromIndex(index), actionName, getTaskViewRoleId(viewId, options.taskViews), options.userInfo.value, viewId, getViewToken(viewId, options.taskViews)) as EntrySubmissionResult;
			if (submissionResult.status === 'Success') {
				options.inlineActionName.value = null;

				let confirmationMessage = submissionResult.confirmationMessage;
				const updatedEntry = await options.getEntry(index);
				confirmationMessage = updatedEntry.toString(confirmationMessage);

				showConfirmationMessage(confirmationMessage);
			}
			else {
				inlineActionFailed = true;
				await options.onTaskClick(index, false);
			}
		}
		catch (error) {
			if (error.message === 'Entry Conflict' && (!error.order && !error.order.IsOpen)) {
				inlineActionFailed = true;
				const result = await options.adminApi.value.performAction(getFormId(index), getEntryKey(error.entry), actionName, getTaskViewRoleId(viewId, options.taskViews), options.userInfo.value, viewId, getViewToken(viewId, options.taskViews)) as EntrySubmissionResult;
				if (result.status === 'Error') {
					await options.onTaskClick(index, true);
				}
			}
			else {
				await options.onTaskClick(index, true);
			}
		}
		finally {
			inlineActionFailed = false;
			inlineActionIndex.value = null;
		}
	};

	const getInlineActionStatus = (index: FormEntryIndex) => {
		if (options.inlineActionName.value && index.Id === inlineActionIndex.value?.Id) {
			if (inlineActionFailed)
				return 'error';
			return 'validating';
		}

		return 'default';
	};

	const showBulkActionDialog = ref(false);
	const showBulkActionDialogContent = ref(false);
	const bulkActionName = ref(null);
	const entriesInView = ref(null);
	const bulkActionRoleId = ref(null);
	const bulkActionViewId = ref(null);
	const bulkActionViewToken = ref(null);
	const performBulkAction = (eventData: { viewId: string, actionName: string }) => {
		const { viewId, actionName } = eventData;
		bulkActionInProgress.value = true;

		bulkActionName.value = actionName;
		entriesInView.value = options.tasks.value.filter(task => getViewId(task) === viewId).map(task => getEntryKeyFromIndex(task));
		bulkActionRoleId.value = getTaskViewRoleId(viewId, options.taskViews);
		bulkActionViewId.value = viewId;
		bulkActionViewToken.value = getViewToken(viewId, options.taskViews);
		showBulkActionDialog.value = true;

		nextTick(() => {
			showBulkActionDialogContent.value = true;
		});
	};

	const resetBulkActionDialogState = () => {
		nextTick(() => {
			showBulkActionDialog.value = false;
		});

		bulkActionName.value = null;
		entriesInView.value = [];
		bulkActionRoleId.value = null;
		bulkActionViewId.value = null;
		bulkActionViewToken.value = null;
	};

	const handleBulkActionComplete = async () => {
		bulkActionInProgress.value = false;
		if (!showBulkActionDialogContent.value)
			resetBulkActionDialogState();
	};

	const handleBulkActionDialogClose = () => {
		showBulkActionDialogContent.value = false;

		if (bulkActionInProgress.value)
			handleBulkActionComplete();
	};

	const isActionInProgress = computed(() => {
		return inlineActionIndex.value || bulkActionInProgress.value;
	});

	return {
		getAllowedActions,
		getAllowedActionsForView,
		performInlineAction,
		getInlineActionStatus,
		performBulkAction,
		showBulkActionDialog,
		bulkActionName,
		bulkActionsMap,
		onActionsLoaded,
		handleLoadAllActions,
		allActionsLoadedMap,
		entriesInView,
		bulkActionRoleId,
		bulkActionViewId,
		bulkActionViewToken,
		handleBulkActionComplete,
		handleBulkActionDialogClose,
		showBulkActionDialogContent,
		isActionInProgress
	};
}
